import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { formatCurrency } from '../charts/d3/format';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { useCardSpend } from '../../hooks/useCardSpend';
import type { AnalyticsPeriod, CardSpendRow } from '../../types/analytics';
import type { SpendCategoryNode } from '../../types/financial';
import { CardDueAlertButton, useSpendAlertCatalog } from './CardDueAlertButton';
import { formatCardTitle, formatDueDate, isDueSoon, moneyOrDash, formatAccountKind, isCreditAccount } from './creditCardFormat';

type Props = {
  period: AnalyticsPeriod;
  periodKey: string;
  sampleCategories: SpendCategoryNode[];
  hasHouseholdSpend: boolean;
  onOpenCard: (card: CardSpendRow) => void;
};

function TotalsStrip({
  spent,
  due,
  min,
  showCreditTotals,
}: {
  spent: number;
  due: number;
  min: number;
  showCreditTotals: boolean;
}) {
  return (
    <View style={styles.totals}>
      <View style={styles.totalCell}>
        <Text style={styles.totalLabel}>Spent</Text>
        <Text style={styles.totalValue}>{formatCurrency(spent)}</Text>
      </View>
      {showCreditTotals ? (
        <>
          <View style={styles.totalCell}>
            <Text style={styles.totalLabel}>Pay in full</Text>
            <Text style={[styles.totalValue, styles.dueValue]}>{formatCurrency(due)}</Text>
          </View>
          <View style={styles.totalCell}>
            <Text style={styles.totalLabel}>Minimum</Text>
            <Text style={styles.totalValue}>{formatCurrency(min)}</Text>
          </View>
        </>
      ) : null}
    </View>
  );
}

/**
 * Extra Spend-tab widget: spend split by the account each transaction was imported from.
 * Combined category spend stays in the original donut / accordion.
 */
export function CreditCardsWidget({
  period,
  periodKey,
  sampleCategories,
  hasHouseholdSpend,
  onOpenCard,
}: Props) {
  const { cards, totals, loading, error, isLive } = useCardSpend({
    period,
    periodKey,
    sampleCategories,
  });
  const alertCatalog = useSpendAlertCatalog();
  const showCreditTotals = cards.some((card) => isCreditAccount(card.type));

  if (isLive && loading && cards.length === 0) {
    return (
      <GlassCard>
        <Text style={styles.title}>Cards</Text>
        <ActivityIndicator color={colors.accent} style={styles.loader} />
      </GlassCard>
    );
  }

  if (cards.length === 0) {
    if (!hasHouseholdSpend) return null;
    return (
      <GlassCard>
        <Text style={styles.title}>Cards</Text>
        <Text style={styles.hint}>
          Upload a credit or debit statement so each transaction is attached to that account.
        </Text>
      </GlassCard>
    );
  }

  return (
    <GlassCard>
      <View style={styles.header}>
        <View style={styles.headerCopy}>
          <Text style={styles.title}>Cards</Text>
          <Text style={styles.subtitle}>
            Spend by account. Credit cards also show pay-in-full and minimum due.
          </Text>
        </View>
        <RefreshButton compact />
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <TotalsStrip
        spent={totals.totalSpend}
        due={totals.totalStatementBalance}
        min={totals.totalMinimumPayment}
        showCreditTotals={showCreditTotals}
      />

      <View style={styles.list}>
        {cards.map((card) => {
          const credit = isCreditAccount(card.type);
          const dueLabel = formatDueDate(card.paymentDueDate);
          const soon = isDueSoon(card.paymentDueDate);
          return (
            <View key={card.spendAccountId} style={styles.row}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`${formatCardTitle(card)}. Spent ${formatCurrency(card.totalSpend)}. Open account details`}
                onPress={() => onOpenCard(card)}
                style={({ pressed }) => [styles.rowMain, pressed && styles.rowPressed]}
              >
                <Text style={styles.cardName} numberOfLines={1}>
                  {formatCardTitle(card)}
                </Text>
                <Text style={[styles.dueHint, soon && styles.dueSoon]}>
                  {formatAccountKind(card.type)}
                  {credit && dueLabel ? ` · Due ${dueLabel}` : ''}
                </Text>
                <View style={styles.metrics}>
                  <Text style={styles.metric}>Spent {formatCurrency(card.totalSpend)}</Text>
                  {credit ? (
                    <>
                      <Text style={styles.metric}>
                        Full {moneyOrDash(card.statementBalance, formatCurrency)}
                      </Text>
                      <Text style={styles.metric}>
                        Min {moneyOrDash(card.minimumPayment, formatCurrency)}
                      </Text>
                    </>
                  ) : null}
                </View>
              </Pressable>
              {credit ? (
                <CardDueAlertButton card={card} catalog={alertCatalog} compact />
              ) : null}
              <Pressable
                onPress={() => onOpenCard(card)}
                accessibilityRole="button"
                accessibilityLabel={`Open ${formatCardTitle(card)}`}
                hitSlop={8}
              >
                <Text style={styles.chevron}>›</Text>
              </Pressable>
            </View>
          );
        })}
      </View>
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  headerCopy: {
    flex: 1,
    gap: 2,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  subtitle: {
    ...typography.caption,
    color: colors.textMuted,
  },
  hint: {
    ...typography.body,
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  error: {
    ...typography.caption,
    color: colors.warning,
    marginBottom: spacing.sm,
  },
  loader: {
    marginVertical: spacing.md,
  },
  totals: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  totalCell: {
    flex: 1,
    backgroundColor: colors.surfaceElevated,
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    gap: 2,
  },
  totalLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  totalValue: {
    ...typography.body,
    color: colors.text,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  dueValue: {
    color: colors.warning,
  },
  list: {
    gap: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 72,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    cursor: 'pointer',
  },
  rowPressed: {
    opacity: 0.88,
  },
  rowMain: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  cardName: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  dueHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  dueSoon: {
    color: colors.warning,
    fontWeight: '600',
  },
  metrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  metric: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  chevron: {
    ...typography.headline,
    color: colors.textMuted,
    fontWeight: '300',
  },
});
